/**
 * Connectivity detection and the app-wide event that publishes it.
 *
 * `navigator.onLine` is not trustworthy on its own: it answers `true` for a device attached to a
 * network with no route to the server (a captive portal, a dropped VPN). It is used here only as
 * a *trigger* to re-check, never as a verdict.
 *
 * The verdict comes from real traffic: the API layer (`plane-api.js`) reports every call. A
 * `TypeError` from `fetch` is a network failure by definition, no response was ever formed; a
 * 4xx or 5xx is a response, and therefore proof the network works.
 *
 * {@link CONNECTIVITY_CHANGED_EVENT} is dispatched on `document.body`, like `session/expired`, so
 * anything in the page can listen without importing this module.
 */
export const CONNECTIVITY_CHANGED_EVENT = 'connectivity/changed';

// The lightest call available: it exists to prove a round trip completes, whatever the answer.
const CONFIRM_PATH = '/api/session';
const INITIAL_BACKOFF_MS = 2000;
const MAX_BACKOFF_MS = 30000;

// Optimistic: most sessions start online, and the first failed call corrects it.
let online = true;
let confirmTimer = null;
let backoffMs = INITIAL_BACKOFF_MS;

function publish() {
  document.body.dispatchEvent(new CustomEvent(CONNECTIVITY_CHANGED_EVENT, { detail: { online } }));
}

function cancelPendingConfirm() {
  if (confirmTimer !== null) {
    clearTimeout(confirmTimer);
    confirmTimer = null;
  }
}

function setState(value) {
  if (value) {
    cancelPendingConfirm();
    backoffMs = INITIAL_BACKOFF_MS;
  }
  if (online === value) {
    return;
  }
  online = value;
  publish();
}

/**
 * The one place that decides "back online": a request has to actually complete. Reschedules
 * itself with a growing backoff while it keeps failing.
 */
async function confirmOnline() {
  confirmTimer = null;
  try {
    await fetch(CONFIRM_PATH, { credentials: 'same-origin' });
    setState(true);
  } catch {
    backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
    confirmTimer = setTimeout(confirmOnline, backoffMs);
  }
}

function scheduleConfirm() {
  if (confirmTimer !== null || online) {
    return;
  }
  confirmTimer = setTimeout(confirmOnline, backoffMs);
}

// `offline` is reliable in the negative (the system has no active interface) and is applied
// directly; `online` only means "worth trying again".
window.addEventListener('offline', () => setState(false));
window.addEventListener('online', () => confirmOnline());

export const connectivity = {
  isOnline: () => online,

  reportNetworkFailure: () => {
    setState(false);
    scheduleConfirm();
  },

  reportNetworkSuccess: () => setState(true),
};
